import { ParsedUrlQuery } from 'querystring';
import { ComponentProps } from 'react';
import styled from 'styled-components';
import {
  CFImage,
  Module,
  NewsItem,
} from '../../lib/types';
import Footer from '../modules/Footer';
import { renderModules } from '../Page';
import ArrowLink from './ArrowLink';
import Card, { timeFormat } from './cards/Card';
import CardGrid from './cards/CardGrid';
import FeaturePostHeader from './FeaturePostHeader';
import ModulePaddingWrapper from './ModulePaddingWrapper';
import Navbar from './Navbar';
import PageMeta from './PageMeta';
import { searchParams } from '../../lib/utils';

export type NewsPageDataProps = {
    pageCount: number,
    currentPage: number,
    currentNews: {[k: string]: NewsItem[]},
    query: ParsedUrlQuery,
    type: string,
}

export const humanFriendlyTypes: {[k: string]: string} = {
  news: 'News',
  'press-release': 'Press Releases',
  event: 'Events',
};

export type NewsPageData = {
  title: string,
  metaDescription: string,
  bottomModules: Module[],
  backgroundImage: CFImage,
  featuredAll: NewsItem,
  featuredNews: NewsItem,
  featuredEvent: NewsItem,
  featuredPressRelease: NewsItem,
}

export type NewsPageProps = {
  title: string,
  metaDescription: string,
  featuredPost: NewsItem,
  bottomModules: Module[],
  backgroundImage: CFImage,
  newsData: NewsPageDataProps,
  navbarData: ComponentProps<typeof Navbar>,
}

const getDateStr = (date: string, endDate?: string) => (
  `${timeFormat.format(new Date(date))}${endDate ? ` - ${timeFormat.format(new Date(endDate))}` : ''}`
);

const getTagsForPost = (post: NewsItem) => {
  const tags: { content: string, color: string }[] = [];
  const endDate = post.endDate || post.date;
  const now = new Date();
  const eventHasEnded = endDate && new Date(endDate) < now;
  const eventIsHappening = endDate && new Date(endDate) > now && new Date(post.date) < now;

  if (post.type === 'event') {
    if (eventHasEnded) {
      tags.push({ content: 'Past event', color: 'var(--color-charcoal-v10)' });
    } else if (eventIsHappening) {
      tags.push({ content: 'Ongoing event', color: 'var(--color-bluescape-v20)' });
    } else {
      tags.push({ content: 'Upcoming event', color: 'var(--color-bluescape-v20)' });
    }
  }

  if (post.location) {
    tags.push({ content: post.location, color: 'var(--color-bluescape-v20)' });
  }

  return tags;
};

function NewsPostItem(props: NewsPageProps['newsData']['currentNews'][''][0]) {
  const {
    title, date, endDate, image, link, slug, type,
  } = props;
  const tags = getTagsForPost(props);

  return (
    <Card
      in2ColumnGrid
      image={image}
      title={title}
      tags={tags}
      date={new Date(date)}
      endDate={endDate ? new Date(endDate) : undefined}
      link={link || `/${type}/${slug || ''}`}
    />
  );
}

const GridContainerContainer = styled.div`
  max-width: 1072px;
  padding: 32px 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  line-height: 120%;
  font-weight: 600;
  margin: 0;
  margin-bottom: 32px;
  margin-top: 48px;
  color: var(--color-bluescape-dark);
  display: flex;
  align-items: center;
  justify-content: space-between;

  *:only-child {
    text-align: center;
    flex: 1;
  }

  a {
    font-size: 0.75em;
  }
`;

const parsedQueryToSearchParams = (query: ParsedUrlQuery) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(
    ([k, v]) => (
      v && (Array.isArray(v) ? v.forEach((j) => params.append(k, j)) : params.append(k, v))
    ),
  );
  return params;
};

const plural = (singular: string) => `${singular}${singular.endsWith('s') ? '' : 's'}`;

const NewsPage = ({
  newsData,
  featuredPost,
  navbarData,
  bottomModules,
  backgroundImage,
  ...pageData
} : NewsPageProps) => {
  const params = parsedQueryToSearchParams(newsData.query);
  params.delete('page');

  const getParamsForPage = (page: number) => {
    const newParams = new URLSearchParams(params);
    if (page > 1) {
      newParams.set('page', page.toString());
    }
    const paramStr = newParams.toString();
    return paramStr.length ? `?${paramStr}` : '';
  };

  const queryStr = searchParams({
    type: newsData.type || undefined,
    page: newsData.currentPage > 1 ? newsData.currentPage : undefined,
  });

  const typePlural = newsData.type && plural(humanFriendlyTypes[newsData.type].toLowerCase());

  const getPageUrl = (pageNumber: number) => (`/news${getParamsForPage(pageNumber)}`);
  const newsParts = Object.entries(newsData.currentNews);

  // Make navbar transparent for all rendered modules
  const navbarProps = {
    ...navbarData,
    transparent: true,
  };
  const ogImgUrl = `${featuredPost.image.url}?fm=png&w=1200&h=630&fit=fill`;
  const renderedBottomModules = renderModules(bottomModules, navbarProps);
  return (
    <div className="theme-charcoal-bleach">
      <PageMeta {...pageData} imageUrl={ogImgUrl} queryString={queryStr} slug="/news" />
      <FeaturePostHeader
        {...featuredPost}
        link={featuredPost.link || `/${featuredPost.type}/${featuredPost.slug || ''}`}
        backgroundImage={backgroundImage}
        navbarData={navbarProps}
        tags={getTagsForPost(featuredPost)}
        date={getDateStr(featuredPost.date, featuredPost.endDate)}
        topLinks={[
          { href: '/news', text: 'All', active: !newsData.type },
          { href: '/news?type=news', text: 'News', active: newsData.type === 'news' },
          { href: '/news?type=press-release', text: 'Press Releases', active: newsData.type === 'press-release' },
          { href: '/news?type=event', text: 'Events', active: newsData.type === 'event' },
        ]}
      />
      <ModulePaddingWrapper theme="Charcoal Bleach">
        <GridContainerContainer>
          {newsParts.map(([type, newsPosts], i) => (
            // eslint-disable-next-line react/no-array-index-key
            <section key={`${type}_${i}`}>
              <SectionTitle>
                <span>{humanFriendlyTypes[type]}</span>
                {newsParts.length > 1 && <ArrowLink title="See all" href={`/news?type=${type}`} />}
              </SectionTitle>
              <CardGrid
                columns={2}
                pagination={
                  newsData.type !== '' ? {
                    page: newsData.currentPage,
                    totalPages: newsData.pageCount,
                    getPageUrl,
                    prevText: `Newer ${typePlural}`,
                    nextText: `Older ${typePlural}`,
                  } : undefined
              }
              >
                {newsPosts.map((newsPost) => (
                  <NewsPostItem key={newsPost.sys.id} {...newsPost} />
                ))}
              </CardGrid>
            </section>
          ))}
        </GridContainerContainer>
      </ModulePaddingWrapper>
      {renderedBottomModules}
      <Footer {...navbarData} />
    </div>
  );
};

export default NewsPage;
